import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "iso-clarity-theme";

const readStoredPreference = (): ThemePreference => {
  if (typeof window === "undefined") return "system";
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === "light" || value === "dark" || value === "system" ? value : "system";
};

const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  if (preference !== "system") return preference;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const applyTheme = (theme: ResolvedTheme, preference: ThemePreference): void => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.dataset.themePreference = preference;
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolveTheme(readStoredPreference()),
  );
  // Track whether the current preference originated locally (user
  // interaction) versus the server. When the server reconciles, we
  // overwrite local; when the user changes it, we push to server.
  const ignoreNextWriteRef = useRef(false);

  const applyPreference = useCallback((next: ThemePreference, persistLocal: boolean) => {
    setPreferenceState(next);
    if (persistLocal && typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
    const nextResolved = resolveTheme(next);
    setResolved(nextResolved);
    applyTheme(nextResolved, next);
  }, []);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      applyPreference(next, true);
      // Best-effort write-through to server; ignore failures (e.g. anonymous).
      void api("/me/preferences", { method: "PATCH", body: { themePreference: next } }).catch(
        () => undefined,
      );
    },
    [applyPreference],
  );

  // Reconcile from server whenever the user becomes authenticated.
  useEffect(() => {
    if (status !== "authenticated") return;
    void api<{ preferences: { themePreference: ThemePreference } }>("/me/preferences")
      .then((res) => {
        const serverPref = res.preferences.themePreference;
        if (serverPref !== preference) {
          ignoreNextWriteRef.current = true;
          applyPreference(serverPref, true);
        }
      })
      .catch(() => undefined);
    // We intentionally don't depend on `preference` here — that would
    // refire reconcile every time the user toggles. We only reconcile on
    // auth-state transition.
  }, [status, applyPreference]);

  // Re-apply when system theme changes while preference is 'system'.
  useEffect(() => {
    if (preference !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const nextResolved = media.matches ? "dark" : "light";
      setResolved(nextResolved);
      applyTheme(nextResolved, preference);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [preference]);

  // Keep DOM in sync if anything else mutates documentElement.
  useEffect(() => {
    applyTheme(resolved, preference);
  }, [resolved, preference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
